/**
 * StatCard premium pour console super-admin.
 * Design pro : fond glassmorphism, icône SVG en pastille, grand chiffre gradient,
 * barre de progression décorative, hover lift.
 */
import { ComponentType, SVGProps } from 'react';

type ColorKey = 'teal' | 'cyan' | 'emerald' | 'amber' | 'rose' | 'violet' | 'indigo';

const COLOR_MAP: Record<ColorKey, {
  iconBg: string;
  iconRing: string;
  iconColor: string;
  ring: string;
  bar: string;
  label: string;
  numberGradient: string;
}> = {
  teal: {
    iconBg: 'bg-teal-500/15',
    iconRing: 'ring-1 ring-teal-400/40',
    iconColor: 'text-teal-300',
    ring: 'hover:ring-teal-400/50',
    bar: 'from-teal-400 to-cyan-400',
    label: 'text-teal-300',
    numberGradient: 'from-teal-200 via-cyan-200 to-white',
  },
  cyan: {
    iconBg: 'bg-cyan-500/15',
    iconRing: 'ring-1 ring-cyan-400/40',
    iconColor: 'text-cyan-300',
    ring: 'hover:ring-cyan-400/50',
    bar: 'from-cyan-400 to-sky-400',
    label: 'text-cyan-300',
    numberGradient: 'from-cyan-200 via-sky-200 to-white',
  },
  emerald: {
    iconBg: 'bg-emerald-500/15',
    iconRing: 'ring-1 ring-emerald-400/40',
    iconColor: 'text-emerald-300',
    ring: 'hover:ring-emerald-400/50',
    bar: 'from-emerald-400 to-teal-400',
    label: 'text-emerald-300',
    numberGradient: 'from-emerald-200 via-teal-200 to-white',
  },
  amber: {
    iconBg: 'bg-amber-500/15',
    iconRing: 'ring-1 ring-amber-400/40',
    iconColor: 'text-amber-300',
    ring: 'hover:ring-amber-400/50',
    bar: 'from-amber-400 to-orange-400',
    label: 'text-amber-300',
    numberGradient: 'from-amber-200 via-orange-200 to-white',
  },
  rose: {
    iconBg: 'bg-rose-500/15',
    iconRing: 'ring-1 ring-rose-400/40',
    iconColor: 'text-rose-300',
    ring: 'hover:ring-rose-400/50',
    bar: 'from-rose-400 to-pink-400',
    label: 'text-rose-300',
    numberGradient: 'from-rose-200 via-pink-200 to-white',
  },
  violet: {
    iconBg: 'bg-violet-500/15',
    iconRing: 'ring-1 ring-violet-400/40',
    iconColor: 'text-violet-300',
    ring: 'hover:ring-violet-400/50',
    bar: 'from-violet-400 to-fuchsia-400',
    label: 'text-violet-300',
    numberGradient: 'from-violet-200 via-fuchsia-200 to-white',
  },
  indigo: {
    iconBg: 'bg-indigo-500/15',
    iconRing: 'ring-1 ring-indigo-400/40',
    iconColor: 'text-indigo-300',
    ring: 'hover:ring-indigo-400/50',
    bar: 'from-indigo-400 to-violet-400',
    label: 'text-indigo-300',
    numberGradient: 'from-indigo-200 via-violet-200 to-white',
  },
};

export interface StatCardProps {
  /** Composant icône SVG (ex: IconBuilding from ./icons) */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  value: number | string;
  color: ColorKey;
  /** Total optionnel pour afficher la barre de progression (value/total) */
  total?: number;
  /** Petite mention (ex : "+2 cette semaine") */
  hint?: string;
  /** Version compacte (moins de padding, chiffre plus petit) */
  compact?: boolean;
}

export default function StatCard({ icon: Icon, label, value, color, total, hint, compact }: StatCardProps) {
  const c = COLOR_MAP[color];
  const numericValue = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
  const pct = total && total > 0 ? Math.min(100, Math.round((numericValue / total) * 100)) : null;

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl bg-white/[0.04] backdrop-blur-xl ring-1 ring-white/10 ${c.ring} ${compact ? 'p-3.5' : 'p-5'} transition-all duration-300 hover:bg-white/[0.07] hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-black/30`}
    >
      {/* Glow décoratif en haut-droite */}
      <div className={`absolute -top-12 -right-12 ${compact ? 'w-24 h-24' : 'w-32 h-32'} rounded-full bg-gradient-to-br ${c.bar} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`} />

      <div className="relative">
        {/* Icône + hint */}
        <div className="flex items-start justify-between gap-2">
          <div className={`${compact ? 'w-8 h-8' : 'w-11 h-11'} rounded-lg ${c.iconBg} ${c.iconRing} ${c.iconColor} flex items-center justify-center shadow-inner`}>
            <Icon className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
          </div>
          {hint && (
            <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-wider text-white/40 font-medium text-right`}>{hint}</span>
          )}
        </div>

        {/* Chiffre */}
        <div className={`${compact ? 'mt-2' : 'mt-3'} flex items-baseline gap-2`}>
          <p className={`${compact ? 'text-2xl' : 'text-4xl'} font-bold tracking-tight bg-gradient-to-br ${c.numberGradient} bg-clip-text text-transparent leading-none`}>
            {value}
          </p>
          {pct !== null && (
            <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-white/40 font-medium`}>/ {total}</span>
          )}
        </div>

        {/* Label */}
        <p className={`${compact ? 'text-[10px] mt-1.5' : 'text-[11px] mt-2'} ${c.label} font-semibold uppercase tracking-[0.15em]`}>{label}</p>

        {/* Barre de progression ou ligne décorative */}
        <div className={`${compact ? 'mt-2' : 'mt-3'} h-1 rounded-full bg-white/10 overflow-hidden`}>
          <div
            className={`h-full rounded-full bg-gradient-to-r ${c.bar} transition-all duration-700`}
            style={{ width: pct !== null ? `${pct}%` : '100%' }}
          />
        </div>
      </div>
    </div>
  );
}
